import type {
  PipelineMilestone,
  PipelineTone,
} from "@/marketing/models/campaignPipeline";

export enum CampaignInfluencerStage {
  FormSent = "Form Sent",
  Shortlisted = "Shortlisted",
  BrandShortlisted = "Brand Shortlisted",
  Shipped = "Shipped",
  Received = "Received",
  Published = "Published",
  Completed = "Completed",
}


export const STAGE_ORDER: CampaignInfluencerStage[] = [
  CampaignInfluencerStage.FormSent,
  CampaignInfluencerStage.Shortlisted,
  CampaignInfluencerStage.Shipped,
  CampaignInfluencerStage.Received,
  CampaignInfluencerStage.Published,
  CampaignInfluencerStage.Completed,
];

export function campaignStageOrder(pipelineType?: string): CampaignInfluencerStage[] {
  return pipelineType === 'performance_seeding'
    ? [CampaignInfluencerStage.FormSent, CampaignInfluencerStage.Shortlisted, CampaignInfluencerStage.BrandShortlisted, ...STAGE_ORDER.slice(2)]
    : STAGE_ORDER;
}

export interface CampaignInfluencerStageMeta {
  label: string;
  tone: PipelineTone;
  milestones: PipelineMilestone[];
}

export const STAGE_META: Record<
  CampaignInfluencerStage,
  CampaignInfluencerStageMeta
> = {
  [CampaignInfluencerStage.FormSent]: {
    label: "Applied",
    tone: "neutral",
    milestones: [],
  },
  [CampaignInfluencerStage.Shortlisted]: {
    label: "Shortlisted",
    tone: "info",
    milestones: ["seat_filled"],
  },
  [CampaignInfluencerStage.BrandShortlisted]: {
    label: "Brand Shortlisted",
    tone: "info",
    milestones: ["seat_filled"],
  },
  [CampaignInfluencerStage.Shipped]: {
    label: "Shipped",
    tone: "progress",
    milestones: ["seat_filled"],
  },
  [CampaignInfluencerStage.Received]: {
    label: "Received",
    tone: "warning",
    milestones: ["seat_filled"],
  },
  [CampaignInfluencerStage.Published]: {
    label: "Published",
    tone: "success",
    milestones: ["seat_filled", "content_posted"],
  },
  [CampaignInfluencerStage.Completed]: {
    label: "Completed",
    tone: "success",
    milestones: ["seat_filled", "content_posted"],
  },
};

export const REJECTED_PILL = "tw-bg-rose-100 tw-text-rose-800";
export const REJECTED_DOT = "tw-bg-rose-600";

// Per-platform attribution for a submitted post (mirrors apps/api ContentSubmission).
export interface ContentSubmission {
  url: string;
  platform: string;
  postId?: string;
  mandatory: boolean;
  submittedAt: string;
  // Per-post TikTok/Instagram Spark Ads (ad authorization) code, used for boosting & reporting.
  sparkAdsCode?: string;
}

export interface CampaignInfluencer {
  campaignId: string;
  influencerId: string;
  userId?: number;
  // Free string validated per pipeline by the backend; resolve labels/tones via the campaign's pipeline definition.
  pipelineStage: string;
  // Absent = standard.
  pipelineType?: string;
  rejected: boolean;
  rejectedAtStage?: string;
  joinedAt: string;
  lastTransitionAt?: string;
  lastTransitionBy?: string;
  platform?: string;
  handle?: string;
  shippingTracking?: string;
  contentSubmissions?: ContentSubmission[];
  // SKUs the creator chose from the campaign pool at apply time (choice-reward campaigns). Empty/undefined for fixed-reward.
  selectedSkus?: string[];
  // CampaignInfluencerTag IDs assigned to this enrollment; resolved to names against the tag catalog.
  tags?: string[];
  // Key of the campaign's operations.batches entry; absent when the creator is in no batch.
  batchId?: string;
  fastTrack: boolean;
}

/** Moves creators into one batch; null takes them out of every batch. */
export interface SetCampaignInfluencersBatchRequest {
  influencerIds: string[];
  batchId: string | null;
}

export interface SetCampaignInfluencersBatchResponse {
  updated: number;
}

export interface AddCampaignInfluencerEntry {
  influencerId: string;
  platform?: string;
  handle?: string;
  selectedSkus?: string[];
}

export interface AddCampaignInfluencersRequest {
  influencers: AddCampaignInfluencerEntry[];
  addedBy: string;
}

export interface AddCampaignInfluencersResponse {
  added: number;
  skipped: number;
  addedInfluencerIds: string[];
}

export interface TransitionStageRequest {
  toStage: string;
  transitionBy: string;
  comment?: string;
  fastTrack?: boolean;
  // When true, the facade suppresses influencer notifications (email/push/in-app) for this transition.
  quiet?: boolean;
}

export interface SetShippingRequest {
  shippingTracking: string;
  transitionBy: string;
}

export interface AdminContentSubmissionInput {
  url: string;
  mandatory: boolean;
}

export interface AddContentSubmissionRequest {
  submission: AdminContentSubmissionInput;
  transitionBy: string;
}

export interface DeleteContentRequest {
  url: string;
  transitionBy: string;
}

export interface SetContentSparkAdsCodeRequest {
  url: string;
  sparkAdsCode?: string;
  transitionBy: string;
}

export interface SetCampaignInfluencerTagsRequest {
  tagIds: string[];
  transitionBy: string;
}

export interface SetRejectedRequest {
  rejected: boolean;
  transitionBy: string;
  reason?: string;
}

export interface SetSelectedSkusRequest {
  selectedSkus: string[];
  setBy: string;
}

export interface BulkImportCampaignInfluencerRow {
  handle: string;
  platform: string;
  stage: string;
  settlementAmount?: number;
  notes?: string;
  selectedSkus?: string[];
  email?: string;
}

export interface BulkImportCampaignInfluencersRequest {
  rows: BulkImportCampaignInfluencerRow[];
  importedBy: string;
}

export interface BulkImportCampaignInfluencersResponse {
  imported: number;
  skipped: number;
  createdInMaster: number;
  errors: string[];
}
